import { api } from '@/global/services/api'
import { isObject } from 'lodash'
import { formatIsoDateCustom, currentDatetime, validateDateTimeFormat, checkDateFormat } from '@/global/services/helpers/dates'
import documentsHistoryHelper from '../documents-history-helper'

const DATE_FORMAT_FROM_REQUEST = 'YYYY-MM-DD HH:mm:ss'
const FORM_DATE_FORMAT = 'DD.MM.YYYY.'
const DATE_FORMAT_TO_SEND = 'YYYY-MM-DD'
const FORM_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'
const DISPLAY_DATETIME_FORMAT = 'DD.MM.YYYY. HH:mm'
const DOCUMENTS_HISTORY_DATA_KEY = 'decisionsDocuments'
const DOCUMENT_TYPE = 'decision'
export default {

  mixins: [
    documentsHistoryHelper
  ],

  data () {
    return {
      decisionsHistoryVisible: false,
      decisionsShowEditButtons: false,
      decisionsEditState: false,
      decisionsShowDocumentPreview: false,
      decisionsDocument: null,
      decisionsDataForGenerationVisible: true,
      decisionsTitle: this.$t('road-maintenance/aggregator.decisions'),
      decisionsBaseTitle: this.$t('road-maintenance/aggregator.decisions'),
      decisionsValidationErrors: {},
      decisionsShowLoader: false,
      decisionsDocumentHistoryLoaded: false,
      decisionsRefreshShowLoader: false,
      decisionsRefreshDisabled: false
    }
  },

  computed: {
    decisionsConfig () {
      return {
        title: this.$t('road-maintenance/aggregator.decisions'),
        visible: this.$route.query.category !== 'request_new',
        key: 'decisions',
        validationErrors: this.decisionsValidationErrors,
        apiConfig: {
          post: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}/store`,
            usePatch: false,
            method: 'post'
          },
          get: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}`,
            method: 'get'
          }
        },
        edit: this.decisionsEditState,
        editUpdate: (event) => this.onDecisionsEditUpdate(event),
        dataFetched: (event) => this.onDecisionsDataFetched(event),
        afterCancel: (event) => this.onDecisionsDataFetched(event),
        afterSave: (event) => this.onDecisionsDataFetched(event, true),
        modifyDataBeforeSend: (event) => this.onDecisionsModifyData(event),
        hiddenFormKeys: [
          'html'
        ],
        tabContent: [
          {
            component: this.tabActionsComponent,
            title: this.decisionsTitle,
            showBackButton: {
              uiViewName: this.$route?.meta?.parent_vue_name ?? null
            },
            showEditButtons: this.decisionsShowEditButtons,
            forceAdditionalButtonsDisplay: true,
            additional_buttons: [
              {
                type: 'base',
                appendIcon: 'mdi-refresh',
                visible: !this.decisionsShowEditButtons,
                showLoader: this.decisionsRefreshShowLoader,
                disabled: this.decisionsRefreshDisabled,
                click: async () => {
                  this.decisionsRefreshShowLoader = true
                  this.decisionsRefreshDisabled = true

                  await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

                  this.decisionsRefreshShowLoader = false
                  setTimeout(() => {
                    this.decisionsRefreshDisabled = false
                  }, this.refreshTabBtnTimeout)
                }
              },
              {
                type: 'base',
                label: this.$t('fleet/documents/prl_cargo.change_status'),
                prependIcon: 'mdi-swap-horizontal',
                visible: !this.decisionsShowEditButtons,
                click: (event) => this.onChangeStatus(event)
              },
              {
                type: 'dropdown',
                buttonType: 'base',
                label: this.$t('road-maintenance/aggregator.generate_document'),
                color: '#65AD45',
                class: 'generate-document-btn',
                visible: !this.decisionsShowEditButtons,
                menuContentClass: 'menu-class',
                component: this.documentsGenerationDropdownMenuComponent,
                hasHistoryData: this.logsData?.[DOCUMENTS_HISTORY_DATA_KEY]?.length,
                menuItemClick: (event) => this.decisionsMenuItemClick(event)
              },
              {
                type: 'base',
                label: this.$t('fleet/documents/prl_cargo.view_pdf'),
                color: '#6e736d',
                visible: this.decisionsShowEditButtons,
                class: 'generate-document-btn',
                click: (event) => this.previewDocument(event, DOCUMENT_TYPE, this.decisionsBaseTitle)
              }
            ],
            onCancelTabSave: () => this.onDecisionsCancelTab()
          },
          // Data for document generation
          {
            component: this.columnsContentComponent,
            title: this.$t('road-maintenance/aggregator.data_for_document_generation'),
            visible: this.decisionsDataForGenerationVisible,
            rows: [
              {
                type: 'fields',
                columnsCount: 4,
                fields: [
                  {
                    key: 'doc_class',
                    label: this.$t('road-maintenance/aggregator.class'),
                    row: 1,
                    column: 1,
                    colSpan: 1.5,
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'registry_number',
                    row: 1,
                    column: 2,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/excavation-service/tabs/approval/columns.registration_number'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'created_at',
                    type: 'datetime-picker',
                    subType: 'date',
                    row: 1,
                    column: 3,
                    colSpan: 1.5,
                    appendIcon: 'mdi-calendar',
                    format: FORM_DATE_FORMAT,
                    placeholder: FORM_DATE_FORMAT,
                    label: this.$t('road-maintenance/excavation-service/tabs/request/columns.request_date'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !validateDateTimeFormat(value, FORM_DATE_FORMAT)) {
                        updateValidationErrors({
                          created_at: this.$t('base.invalid_date').replace(':format', FORM_DATE_FORMAT)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: true,
                    doubleClickEdit: false
                  },
                  {
                    key: 'applicant_name',
                    row: 2,
                    column: 1,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/prp.request_applicant'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'request_street_name',
                    row: 2,
                    column: 2,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.request_address_street'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'request_house_number',
                    row: 2,
                    column: 3,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.request_address_house_number'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'work_type',
                    type: 'textarea',
                    row: 3,
                    column: 1,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.type_of_works_or_actions'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'regulation_setter',
                    row: 3,
                    column: 2,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/codebook_type.regulation_setter'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_name',
                    row: 3,
                    column: 3,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.contractor_name'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_responsible',
                    row: 4,
                    column: 1,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.contractor_responsible_person'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_phone',
                    row: 4,
                    column: 2,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.contractor_phone'),
                    creatable: true,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_email',
                    row: 4,
                    column: 3,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.contractor_email'),
                    creatable: true,
                    editable: false,
                    visible: true
                  },
                  {
                    type: 'plain-text',
                    text: this.$t('road-maintenance/aggregator.road_closure_will_be_closed'),
                    row: 5,
                    column: 1,
                    colSpan: 2,
                    class: 'time-period-text',
                    colClass: 'time-period-column',
                    visible: true
                  },
                  {
                    type: 'plain-text',
                    text: this.$t('road-maintenance/aggregator.requested_date_from_request'),
                    row: 5,
                    column: 2,
                    colSpan: 2,
                    class: 'time-period-text',
                    colClass: 'time-period-column',
                    visible: true
                  },
                  {
                    key: 'valid_from',
                    type: 'datetime-picker',
                    row: 6,
                    column: 1,
                    colSpan: 1.5,
                    appendIcon: 'mdi-calendar-month',
                    format: DISPLAY_DATETIME_FORMAT,
                    placeholder: DISPLAY_DATETIME_FORMAT,
                    label: this.$t('road-maintenance/aggregator.date_from'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !validateDateTimeFormat(value, DISPLAY_DATETIME_FORMAT)) {
                        updateValidationErrors({
                          valid_from: this.$t('base.invalid_date').replace(':format', DISPLAY_DATETIME_FORMAT)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: true,
                    doubleClickEdit: false
                  },
                  {
                    key: 'valid_to',
                    type: 'datetime-picker',
                    row: 6,
                    column: 2,
                    colSpan: 1.5,
                    appendIcon: 'mdi-calendar-month',
                    format: DISPLAY_DATETIME_FORMAT,
                    placeholder: DISPLAY_DATETIME_FORMAT,
                    label: this.$t('road-maintenance/aggregator.date_to'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !validateDateTimeFormat(value, DISPLAY_DATETIME_FORMAT)) {
                        updateValidationErrors({
                          valid_to: this.$t('base.invalid_date').replace(':format', DISPLAY_DATETIME_FORMAT)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: true,
                    doubleClickEdit: false
                  },
                  {
                    key: 'date_from',
                    row: 6,
                    column: 3,
                    colSpan: 1.5,
                    appendIcon: 'mdi-calendar-month',
                    label: this.$t('road-maintenance/aggregator.date_from'),
                    onInput: (value, event) => {
                      const { updateData } = event || {}
                      if (value && typeof updateData === 'function') {
                        updateData({
                          valid_from: value
                        })
                      }
                    },
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'date_to',
                    row: 6,
                    column: 4,
                    colSpan: 1.5,
                    appendIcon: 'mdi-calendar-month',
                    label: this.$t('road-maintenance/aggregator.date_to'),
                    onInput: (value, event) => {
                      const { updateData } = event || {}
                      if (value && typeof updateData === 'function') {
                        updateData({
                          valid_to: value
                        })
                      }
                    },
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'detour',
                    type: 'textarea',
                    row: 7,
                    column: 1,
                    colSpan: 2,
                    label: this.$t('road-maintenance/aggregator.detours_during_works'),
                    unlock: true,
                    doubleClickEdit: false,
                    creatable: true,
                    editable: true,
                    visible: true
                  },
                  {
                    key: 'amendments',
                    type: 'textarea',
                    row: 7,
                    column: 2,
                    colSpan: 2,
                    label: this.$t('road-maintenance/aggregator.necessary_amendments_to_decision'),
                    unlock: true,
                    doubleClickEdit: false,
                    creatable: true,
                    editable: true,
                    visible: true
                  },
                  {
                    key: 'document_delivery_participants',
                    type: 'textarea',
                    row: 8,
                    column: 1,
                    colSpan: 4,
                    label: this.$t('road-maintenance/aggregator.other_participants_for_document'),
                    unlock: true,
                    doubleClickEdit: false,
                    creatable: true,
                    editable: true,
                    visible: true
                  }
                ]
              }
            ]
          },
          // Document history
          {
            component: this.rowBlocksDataComponent,
            mainClasses: 'aggregator-requests-blocks-main',
            wrapperClasses: 'aggregator-requests-subject-wrapper',
            title: this.$t('road-maintenance/aggregator.log_history'),
            visible: this.decisionsHistoryVisible,
            externalData: this.logsData,
            dataKey: DOCUMENTS_HISTORY_DATA_KEY,
            showLoader: this.logsShowLoader,
            columnsConfig: [
              {
                key: 'created_at',
                valueClass: 'history-block-value',
                title: this.$t('road-maintenance/aggregator.application_submission_date') + ':',
                customValue: (data) => {
                  return data?.created_at ? formatIsoDateCustom(data.created_at, FORM_DATETIME_FORMAT, DISPLAY_DATETIME_FORMAT) : ''
                }
              },
              {
                key: 'status',
                valueClass: 'history-block-value',
                title: this.$t('road-maintenance/aggregator.request_type') + ':',
                customValue: (data) => {
                  return data?.status ? this.$t(data.status) : '-'
                }
              },
              {
                key: 'user',
                valueClass: 'history-block-value',
                title: this.$t('road-maintenance/aggregator.request_by') + ':'
              },
              {
                key: 'document',
                valueClass: 'history-block-value',
                title: this.$t('road-maintenance/aggregator.document') + ':',
                prependImage: {
                  src: '/img/icons/pdf-icon.svg',
                  style: {
                    marginRight: '4%'
                  }
                },
                contentWrapperStyle: {
                  display: 'flex',
                  alignItems: 'center'
                },
                customValue: (data) => {
                  return data?.document?.name ? data.document.name : '-'
                },
                click: async (event) => await this.downloadDocument(event)
              }
            ]
          },
          {
            component: this.documentPreviewComponent,
            visible: this.decisionsShowDocumentPreview,
            content: this.decisionsDocument,
            showLoader: this.decisionsShowLoader
          }
        ]
      }
    }
  },

  methods: {
    async onDecisionsGenerateDocument (event, showLast = false) {
      try {
        const { formData } = event || {}

        if (formData) {
          // Create formData object for sending
          const dataToSend = new FormData()
          dataToSend.append('detour', formData?.detour ?? '')
          dataToSend.append('amendments', formData?.amendments ?? '')
          dataToSend.append('document_delivery_participants', formData?.document_delivery_participants ?? '')
          dataToSend.append('created_at', formData?.created_at ? formatIsoDateCustom(formData.created_at, FORM_DATE_FORMAT, DATE_FORMAT_TO_SEND) : '')
          dataToSend.append('valid_from', formData?.valid_from ? formatIsoDateCustom(formData.valid_from, DISPLAY_DATETIME_FORMAT, DATE_FORMAT_FROM_REQUEST) : '')
          dataToSend.append('valid_to', formData?.valid_to ? formatIsoDateCustom(formData.valid_to, DISPLAY_DATETIME_FORMAT, DATE_FORMAT_FROM_REQUEST) : '')
          dataToSend.append('show_last_version', showLast)

          // Get document html
          const { data } = await api('road-maintenance').post(`traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}`, dataToSend)

          this.decisionsDocument = data
          this.decisionsShowDocumentPreview = true
          this.decisionsEditState = true
          this.decisionsShowEditButtons = true
          this.decisionsDataForGenerationVisible = false
          this.decisionsTitle = this.$t('road-maintenance/aggregator.decision_document_overview')
        }
      }
      catch (exception) {
        const { errors } = exception?.response?.data || {}
        this.decisionsShowLoader = false
        this.decisionsValidationErrors = errors && isObject(errors) ? errors : {}
        console.error(exception)
      }
    },

    onDecisionsCancelTab () {
      this.$set(this.informationDialogConfig, 'title', this.$t('base.warning'))
      this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.confirm'))
      this.$set(this.informationDialogConfig, 'cancelButtonText', this.$t('base.cancel'))
      this.$set(this.informationDialogConfig, 'message', this.$t('fleet/vehicle.data_not_saved_message'))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.decisionsCancel())
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    decisionsCancel () {
      this.decisionsEditState = false
      this.$set(this.informationDialogConfig, 'showDialog', false)
    },

    onDecisionsEditUpdate (event) {
      const { edit } = event || {}
      this.decisionsHistoryVisible = !edit

      if (!edit) {
        this.decisionsShowDocumentPreview = false
        this.decisionsEditState = false
        this.decisionsShowEditButtons = false
        this.decisionsDataForGenerationVisible = true
        this.decisionsDocument = null
        this.decisionsTitle = this.decisionsBaseTitle
      }
    },

    async onDecisionsDataFetched (event, afterSave = false) {
      const { data: apiData, updateData } = event || {}
      const { formDataForSend } = event || {}

      const data = apiData?.data || formDataForSend || {}

      if (typeof updateData === 'function' && data && isObject(data)) {
        updateData({
          // Update contractor
          contractor_name: data?.contractor?.name ?? null,
          contractor_responsible: data?.contractor?.responsible_person ?? null,
          contractor_phone: data?.contractor?.phone ?? null,
          contractor_email: data?.contractor?.email ?? null,

          // Update applicant
          applicant_name: (data?.applicant?.first_name ?? '') + ' ' + (data?.applicant?.last_name ?? ''),

          // Update data from request
          request_street_name: data?.geolocation?.address_street ?? null,
          request_house_number: data?.geolocation?.address_number ?? null,

          // Regulation setter
          regulation_setter: data?.regulation_setter?.description ? this.$t(data.regulation_setter.description) : null,

          // date from and to
          date_from: data?.date_from && data?.time_from ? data?.date_from + ' ' + data?.time_from : null,
          date_to: data?.date_to && data?.time_to ? data?.date_to + ' ' + data?.time_to : null
        })

        // Modify a created_at date to be in format: FORM_DATE_FORMAT only if it is not already in FORM_DATE_FORMAT
        if (data && data.created_at && !checkDateFormat(data.created_at, FORM_DATE_FORMAT)) {
          updateData({
            created_at: data?.created_at ? formatIsoDateCustom(data.created_at, DATE_FORMAT_FROM_REQUEST, FORM_DATE_FORMAT) : currentDatetime(FORM_DATE_FORMAT)
          })
        }
      }

      if (!this.decisionsDocumentHistoryLoaded || afterSave) {
        this.decisionsDocumentHistoryLoaded = true
        const { id: suggestedStatusId, description } = data?.suggested_status || {}

        // Fetch documents logs
        await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

        // Open change status popup after documents save only if we have suggested status provided
        if (suggestedStatusId) {
          this.$set(this.afterDocumentStatusChangeActions, 'statusId', suggestedStatusId)
          const message = this.$t('road-maintenance/aggregator.status_change_message')
          this.$set(this.informationDialogConfig, 'showDialog', afterSave)
          this.$set(this.informationDialogConfig, 'message', '<div> ' + message + '<span style="font-style: italic; color: green;">' + ' ' + this.$t(description) + '</span>?</div>')
        }
      }
    },

    onDecisionsModifyData (event) {
      const { originalData } = event || {}

      if (originalData) {
        originalData.created_at = originalData?.created_at ? formatIsoDateCustom(originalData.created_at, FORM_DATE_FORMAT, DATE_FORMAT_TO_SEND) : null
        originalData.valid_from = originalData?.valid_from ? formatIsoDateCustom(originalData.valid_from, DISPLAY_DATETIME_FORMAT, DATE_FORMAT_FROM_REQUEST) : null
        originalData.valid_to = originalData?.valid_to ? formatIsoDateCustom(originalData.valid_to, DISPLAY_DATETIME_FORMAT, DATE_FORMAT_FROM_REQUEST) : null
      }

      return originalData
    },

    async decisionsMenuItemClick (event) {
      const { menuIndex } = event || {}
      await this.onDecisionsGenerateDocument(event, menuIndex === 1)
    }
  }
}
